type EnumLikeEntries = number | string | symbol;
/**
 * as const with arbitrary support functions
 */
type EnumLike = {
  [prop: string]: EnumLikeEntries | Function;
};

// Entry helper method names
const EnumLikeFor = "for";
const EnumLikeOrdinal = "ordinal";
const EnumLikeName = "name";
// Debug helper method names
const EnumLikeInfo = "info";
const EnumLikeTrace = "trace";

/**
 * helper function for as const enum provides common functions
 * and allows addition of enum relevant functions
 * 
```
import { EnumLikeAsConst } from "./enum-like-as-const";

const Red = 0;
const Green = 1;
const Blue = 2

type RGBEntries =
  | typeof Red
  | typeof Green
  | typeof Blue;
// handwritten to break circular ref.
export type RGB = RGBEntries | ((v: RGBEntries) => boolean);

export const RGB = {
  Red: Red,
  Green: Green,
  Blue: Blue,
  ...EnumLikeAsConst.stub<RGB>(),
} as const;

EnumLikeAsConst.define(RGB, "RGB");
```
 */
export class EnumLikeAsConst {
  static define(enumLike: EnumLike, enumName: string): void {
    const enumEntries = new Map<number | string | symbol, string>();

    Object.getOwnPropertyNames(enumLike).forEach((prop) => {
      const desc = Object.getOwnPropertyDescriptor(enumLike, prop);
      if (EnumLikeAsConst.isEnumLikeValue(desc.value)) return;

      desc.enumerable = false;

      Object.defineProperty(enumLike, prop, desc);
    });

    Object.keys(enumLike).forEach((key) => {
      let v = enumLike[key] as unknown as EnumLikeEntries;
      enumEntries.set(v, key);
    });

    Object.defineProperty(enumLike, Symbol.toStringTag, {
      get: () => enumName,
    });

    Object.defineProperty(enumLike, EnumLikeFor, {
      value: (v?: any) => {
        if (EnumLikeAsConst.isEnumLikeValue(v))
          return [...enumEntries.keys()].find((entry) => entry === v);
      },
      writable: false,
      configurable: true,
      enumerable: false,
    });

    Object.defineProperty(enumLike, EnumLikeOrdinal, {
      value: (v?: any) => {
        if (EnumLikeAsConst.isEnumLikeValue(v))
          return [...enumEntries.keys()].findIndex((entry) => entry === v);
      },
      writable: false,
      configurable: true,
      enumerable: false,
    });

    Object.defineProperty(enumLike, EnumLikeName, {
      value: (t: any, value?: any) => {
        if (Array.isArray(t) && EnumLikeAsConst.isEnumLikeValue(value))
          return `${enumEntries.get(value)}`;

        if (EnumLikeAsConst.isEnumLikeValue(t)) return `${enumEntries.get(t)}`;
      },
      writable: false,
      configurable: true,
      enumerable: false,
    });

    Object.defineProperty(enumLike, EnumLikeInfo, {
      value: (t: any, value?: any) => {
        if (Array.isArray(t) && EnumLikeAsConst.isEnumLikeValue(value))
          return `${enumName}.${String(enumEntries.get(value))}`;

        if (EnumLikeAsConst.isEnumLikeValue(t)) return `${enumName}.${String(enumEntries.get(t))}`;
      },
      writable: false,
      configurable: true,
      enumerable: false,
    });

    Object.defineProperty(enumLike, EnumLikeTrace, {
      value: (t: any, value?: any) => {
        if (Array.isArray(t) && EnumLikeAsConst.isEnumLikeValue(value))
          return `${enumName}.${String(enumEntries.get(value))}: ${String(value)}`;

        if (EnumLikeAsConst.isEnumLikeValue(t))
          return `${enumName}.${String(enumEntries.get(t))}: ${String(t)}`;
      },
      writable: false,
      configurable: true,
      enumerable: false,
    });
  }

  private static isEnumLikeValue(value: any): boolean {
    return typeof value === "string" || typeof value === "number" || typeof value === "symbol";
  }

  private static stubDebug<T>(): (t: TemplateStringsArray | T, value?: T) => string {
    return (_t: unknown | T, _value?: T) => ``;
  }

  private static stubValue<T>(): (value?: T) => T {
    return (_value?: T) => undefined;
  }

  private static stubName<T>(): (t: TemplateStringsArray | T, value?: T) => string {
    return (_t: unknown | T, _value?: T) => ``;
  }

  static stub<T>() {
    return {
      [EnumLikeFor]: EnumLikeAsConst.stubValue<T>(),
      [EnumLikeInfo]: EnumLikeAsConst.stubDebug<T>(),
      [EnumLikeName]: EnumLikeAsConst.stubName<T>(),
      [EnumLikeOrdinal]: EnumLikeAsConst.stubValue<T>(),
      [EnumLikeTrace]: EnumLikeAsConst.stubDebug<T>(),
    };
  }
}
